import { apiClient, apiUrl } from '../utils/apiClient'

import { setActiveOrganizationId } from '../utils/activeOrganization'
import { getSessionToken, setSessionToken } from '../auth/session'

/** Persists `?invite=` from login URL until the user accepts the organization invite. */
export const ORG_INVITE_LS_KEY = 'veilstream_pending_org_invite'

export type OrgInvitePreview = {
  organization_name: string
  role: string
  expires_at: string
}

export type OrgInviteAcceptResult = {
  message: string
  organization_id: number
  organization_name: string
  role: string
  already_member?: boolean
  token?: string
}

export function persistOrgInviteTokenFromURL(searchParams: URLSearchParams): boolean {
  const raw = searchParams.get('invite')
  if (raw == null || raw.trim() === '') {
    return false
  }
  localStorage.setItem(ORG_INVITE_LS_KEY, raw.trim())
  return true
}

export function peekPendingOrgInviteToken(): string | null {
  const raw = localStorage.getItem(ORG_INVITE_LS_KEY)
  return raw?.trim() ? raw.trim() : null
}

export async function fetchOrgInvitePreview(token: string): Promise<OrgInvitePreview> {
  return apiClient.get<OrgInvitePreview>(`/api/organization/invite/${encodeURIComponent(token)}/preview`)
}

export async function acceptOrgInvite(token: string): Promise<OrgInviteAcceptResult> {
  const authToken = getSessionToken()
  if (!authToken?.trim()) {
    throw new Error('Not authenticated')
  }

  const response = await fetch(
    apiUrl(`/api/organization/invite/${encodeURIComponent(token)}/accept`),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: '{}',
    },
  )

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(typeof data?.error === 'string' ? data.error : 'Failed to accept invitation')
  }
  return data as OrgInviteAcceptResult
}

/** Accepts a pending invite if present; clears localStorage on success. */
export async function flushPendingOrgInvite(): Promise<OrgInviteAcceptResult | null> {
  const token = peekPendingOrgInviteToken()
  if (!token) return null

  const authToken = getSessionToken()
  if (!authToken?.trim()) return null

  const accepted = await acceptOrgInvite(token)
  localStorage.removeItem(ORG_INVITE_LS_KEY)
  if (accepted.token) {
    setSessionToken(accepted.token)
  }
  if (accepted.organization_id) {
    setActiveOrganizationId(accepted.organization_id)
  }
  return accepted
}
